import Util from '../../../shared/util/util';
import {StageLike} from '../../../shared/util/projectStageUtil';

type ProjectFieldValue = string | number | null | undefined;

type FilterableProjectNode = {
	id?: string;
	companyProjectId?: ProjectFieldValue;
	name?: string | null;
	client?: {name?: string | null} | null;
	forecast?: ProjectFieldValue;
	reported?: ProjectFieldValue;
	remaining?: ProjectFieldValue;
	budget?: ProjectFieldValue;
	customProjectId?: ProjectFieldValue;
	readOnlyAccess?: boolean | null;
	showProject?: boolean | string | null;
	stage?: StageLike;
};

export type FilterableProjectEdge = {node: FilterableProjectNode};

type FilterableProjectGroupNode = {
	id?: string;
	companyProjectGroupId?: ProjectFieldValue;
	name?: string | null;
	readOnlyAccess?: boolean | null;
	projects?: {edges: FilterableProjectEdge[]} | null;
};

type FilterableEdge = {node: FilterableProjectNode & FilterableProjectGroupNode};

type CalculatedDataMap = Map<string | undefined, {completion?: ProjectFieldValue}>;

type FilterFunctions = {
	projectFilter?: (project: FilterableProjectNode, options: {completion: ProjectFieldValue}) => boolean;
} | null;

const projectGroupSearchFilter = (projectGroup: FilterableEdge, searchFilterValue: string): boolean => {
	const origin = 'X' + projectGroup.node.companyProjectGroupId + projectGroup.node.name;
	return Util.normalizedIncludes(origin, searchFilterValue);
};

const searchFilter = (
	project: FilterableProjectEdge,
	searchFilterValue: string,
	calculatedDataMap: CalculatedDataMap | null | undefined
): boolean => {
	const projectCompletion =
		calculatedDataMap && calculatedDataMap.has(project.node.id) ? calculatedDataMap.get(project.node.id)?.completion : '';
	const origin =
		'P' +
		project.node.companyProjectId +
		project.node.name +
		(project.node.client ? project.node.client.name : '') +
		projectCompletion +
		project.node.forecast +
		project.node.reported +
		project.node.remaining +
		project.node.budget +
		project.node.customProjectId;

	return Util.normalizedIncludes(origin, searchFilterValue);
};

const projectsFilter = (
	project: FilterableProjectEdge,
	_filters: unknown,
	filterFunctions: FilterFunctions,
	calculatedDataMap: CalculatedDataMap
): boolean => {
	if (filterFunctions && filterFunctions.projectFilter) {
		const options = {
			completion: calculatedDataMap.has(project.node.id) ? calculatedDataMap.get(project.node.id)?.completion : null,
		};
		return filterFunctions.projectFilter(project.node, options);
	}
	return true;
};

const hasAccessToProject = (p: FilterableProjectEdge): boolean => !p.node.readOnlyAccess;

export const filterProjects = <T extends FilterableEdge>(
	projects: T[],
	searchFilterValue: string,
	filters: unknown,
	filterFunctions: FilterFunctions,
	calculatedDataMap: CalculatedDataMap
): T[] => {
	return projects.filter(project => {
		const isProjectGroup = project.node.projects;
		let showProject;

		if (isProjectGroup) {
			let hasMatchingProjects = false;
			const groupSearchMatch = searchFilterValue && projectGroupSearchFilter(project, searchFilterValue);
			isProjectGroup.edges.forEach(p => {
				const projectMatchesFilter = projectsFilter(p, filters, filterFunctions, calculatedDataMap);
				const projectMatchesSearch = searchFilter(p, searchFilterValue, calculatedDataMap);
				// show child project if it matches the filter and either the project or the group matches the search
				p.node.showProject = projectMatchesFilter && (projectMatchesSearch || groupSearchMatch);

				if (p.node.showProject && hasAccessToProject(p)) {
					hasMatchingProjects = true;
				}
			});
			showProject = hasMatchingProjects;
		} else {
			showProject =
				hasAccessToProject(project) &&
				projectsFilter(project, filters, filterFunctions, calculatedDataMap) &&
				searchFilter(project, searchFilterValue, calculatedDataMap);
		}
		return showProject;
	});
};
